import { Injectable } from '@angular/core';
import { ApiResponse, LS_KEYS } from '@contracts';
import { ApiCilent, SessionStore } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, interval } from 'rxjs';

export type Lang = 'th' | 'en';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private refreshing = false;
  private readonly TTL = 24 * 60 * 60 * 1000;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  constructor(
    private apiCilent: ApiCilent,
    private translate: TranslateService,
    private sessionStore: SessionStore,
  ) {}

  async init() {
    try {
      const lang = this.getLang();

      this.translate.setDefaultLang('th');
      this.translate.use(lang);

      const cached = this.getLocal();

      if (cached && !this.isExpired()) {
        const mapped = this.mapLang(cached, lang);
        this.translate.setTranslation(lang, mapped, true);
      } else {
        await this.refresh();
      }

      this.scheduleRefresh();
    } catch (err) {
      console.error('❌ i18n init failed:', err);
    }
  }

  async refresh(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;

    try {
      const res = await firstValueFrom(
        this.apiCilent.get<ApiResponse<any>>({
          path: 'view_value',
          domain: 'fast',
          version: 'v1',
        }),
      );

      const raw = res?.meta.response_data;

      const serverTime = res?.meta.response_datetime;

      if (!raw) {
        console.error('❌ response_data not found');
        return;
      }

      this.saveLocal(raw, serverTime);

      const lang = this.getLang();
      const mapped = this.mapLang(raw, lang);

      this.translate.setTranslation(lang, mapped, true);
    } catch (err) {
      console.error('❌ i18n refresh failed:', err);
    } finally {
      this.refreshing = false;
    }
  }

  switchLang(lang: Lang) {
    this.sessionStore.setLanguage(lang);

    this.translate.use(lang);

    const cached = this.getLocal();

    if (cached) {
      const mapped = this.mapLang(cached, lang);
      this.translate.setTranslation(lang, mapped, true);
    } else {
      this.refresh();
    }
  }

  private mapLang(data: any, lang: Lang): Record<string, string> {
    const result: Record<string, string> = {};

    const merge = (section: Record<string, any>, prefix?: string) => {
      Object.keys(section || {}).forEach((key) => {
        const value = section[key]?.[lang] || key;
        const finalKey = prefix ? `${prefix}.${key}` : key;
        result[finalKey] = value;
      });
    };

    merge(data.view_values, 'view_value');
    merge(data.dictionaries_validate, 'dict_valid');

    return result;
  }

  private getLocal(): any | null {
    const raw = localStorage.getItem(LS_KEYS.i18.value);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private saveLocal(data: any, datetime: string) {
    localStorage.setItem(LS_KEYS.i18.value, JSON.stringify(data));
    localStorage.setItem(LS_KEYS.i18.date, datetime);
  }

  private isExpired(): boolean {
    const raw = localStorage.getItem(LS_KEYS.i18.date);
    if (!raw) return true;

    const last = this.parseDate(raw);

    const lastDate = new Date(last);
    const nowDate = new Date();

    const isNewDay =
      lastDate.getDate() !== nowDate.getDate() ||
      lastDate.getMonth() !== nowDate.getMonth() ||
      lastDate.getFullYear() !== nowDate.getFullYear();

    return isNewDay;
  }

  private parseDate(dateStr: string): number {
    const normalized = dateStr.replace(',', '');
    return new Date(normalized + ' GMT+0700').getTime();
  }

  private getLang(): Lang {
    return (localStorage.getItem('lang') as Lang) || 'th';
  }

  private scheduleRefresh() {
    clearTimeout(this.refreshTimer);
    const now = new Date();

    const nextMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0,
      0,
    );

    const ms = nextMidnight.getTime() - now.getTime();

    setTimeout(async () => {
      await this.refresh();
      this.scheduleRefresh();
    }, ms);
  }
}
